import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Chip,
  Collapse,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useHistory } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { platformState } from "../global-state";
import { useAppInterface } from "../app/app-interface-provider";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import { useToast } from "../common/toast";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MSwitch from "../common/switch";

const PhonePermission = ({ forceShow = false }) => {
  const history = useHistory();
  const platform = useRecoilValue(platformState);
  const isApp = platform?.source === "app" || forceShow;
  const appInterface = useAppInterface();
  const [phoneInfo, setPhoneInfo] = useState(null);
  const [fullCategories, setFullCategories] = useState([]);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [notificationPriorities, setNotificationPriorities] = useState([
    "high",
  ]);
  const [dataReportMode, setDataReportMode] = useState("default");
  const [dataReportLoading, setDataReportLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState({});
  const [capabilityLoading, setCapabilityLoading] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const isSmallScreen = useIsSmallScreen();
  const { successToast, errorToast } = useToast();

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  // Helper function to convert MUI spacing to pixels
  const spacingToPx = (value) => {
    if (typeof value === "number") {
      return `${value * 8}px`;
    }
    return value;
  };

  const loadPhoneInfo = async () => {
    if (appInterface.isAvailable()) {
      const phoneInfoJson = await appInterface.getPhoneInfo();
      console.debug("phoneInfoJson", phoneInfoJson);
      const phoneData = phoneInfoJson;
      setPhoneInfo(phoneData);
      const categoriesJson = await appInterface.getPhoneCategories();
      setFullCategories(categoriesJson);

      // Load notification settings
      const notificationInfo = phoneData.notificationSetting;
      if (notificationInfo) {
        setNotificationEnabled(notificationInfo.enabled || false);
        setNotificationPriorities(
          notificationInfo.levels || ["high", "low", "dialog"]
        );
      }

      // Load data report mode
      setDataReportMode(phoneData.dataReportMode || "default");
    } else {
      setPhoneInfo(null);
      setFullCategories([]);
      setNotificationEnabled(false);
      setNotificationPriorities(["high"]);
      setDataReportMode("default");
    }
  };

  const handlePhoneToggle = async (enable) => {
    setToggleLoading(true);
    // Allow UI to update before making blocking call
    setTimeout(async () => {
      const success = await appInterface.enableDisablePhone(enable);
      if (success) {
        await loadPhoneInfo();
      } else {
        errorToast(`Error!`);
      }
      setToggleLoading(false);
    }, 0);
  };

  const handleNotificationToggle = async (enable) => {
    setNotificationLoading(true);
    setTimeout(async () => {
      const settings = {
        enabled: enable,
        levels: enable ? notificationPriorities : [],
      };
      const success = await appInterface.setNotificationSetting(settings);
      if (success) {
        setNotificationEnabled(enable);
        if (!enable) {
          // Reset priorities when disabling
          setNotificationPriorities(["high", "low", "dialog"]);
        }
      } else {
        errorToast(`Error!`);
      }
      setNotificationLoading(false);
    }, 0);
  };

  const handleNotificationPriorityChange = async (priority, checked) => {
    let newPriorities;
    if (checked) {
      // Add priority if not already included
      newPriorities = [...notificationPriorities, priority].filter(
        (p, index, arr) => arr.indexOf(p) === index
      );
    } else {
      newPriorities = notificationPriorities.filter((p) => p !== priority);
    }
    const settings = {
      enabled: notificationEnabled,
      levels: newPriorities,
    };
    const success = await appInterface.setNotificationSetting(settings);
    if (success) {
      setNotificationPriorities(newPriorities);
    } else {
      errorToast(`Error!`);
    }
  };

  const handleCategoryToggle = async (categoryId, enable) => {
    setCategoryLoading((prev) => ({ ...prev, [categoryId]: true }));
    // Allow UI to update before making blocking call
    setTimeout(async () => {
      const success = await appInterface.setPhoneDataCategory(
        categoryId,
        enable
      );
      if (success) {
        await loadPhoneInfo();
      } else {
        errorToast(`Error!`);
      }
      setCategoryLoading((prev) => ({ ...prev, [categoryId]: false }));
    }, 0);
  };

  const handleCapabilityToggle = async (capabilityId, enabled) => {
    setCapabilityLoading((prev) => ({ ...prev, [capabilityId]: true }));
    // Allow UI to update before making blocking call
    setTimeout(async () => {
      try {
        const success = await appInterface.setPhoneDataCapability(
          capabilityId,
          enabled
        );
        if (success) {
          await loadPhoneInfo();
        } else {
          errorToast(`Error!`);
        }
      } catch (error) {
        console.error("Error toggling capability:", error);
        errorToast(`Error!`);
      }
      setCapabilityLoading((prev) => ({ ...prev, [capabilityId]: false }));
    }, 0);
  };

  const handleDataReportModeChange = async (mode) => {
    setDataReportLoading(true);
    setTimeout(async () => {
      try {
        const resp = await appInterface.setPhoneDataReportMode(mode);
        if (resp && resp.success) {
          setDataReportMode(mode);
        } else {
          errorToast(`Error!`);
        }
      } catch (error) {
        console.error("Error setting data report mode:", error);
        errorToast(`Error!`);
      }
      setDataReportLoading(false);
    }, 0);
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Check if category is enabled
  const isCategoryEnabled = (categoryId) => {
    return phoneInfo?.categories?.includes(categoryId) || false;
  };

  const isCapabilityEnabled = (capabilityId) => {
    return phoneInfo?.supportedCapabilities?.includes(capabilityId) || false;
  };

  // Load phone info on component mount
  useEffect(() => {
    if (isApp) {
      loadPhoneInfo().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isApp]);

  // Show message if not in app and not forced to show
  if (!isApp && !forceShow) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          This feature is only available in the app.
        </Typography>
      </div>
    );
  }

  // Show loading indicator while initially loading phone info
  if (loading) {
    return <></>;
  }

  const isActive = phoneInfo?.status === "active";
  const deviceName = phoneInfo?.deviceName || "This Device";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        height: "100%",
        padding: spacingToPx(2),
        width: "100%",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {/* Main Device Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
          }}
        >
          <CardContent>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body1" color="primary">
                {deviceName}
              </Typography>
              {toggleLoading ? (
                <CircularProgress size={24} />
              ) : (
                <MSwitch
                  checked={isActive}
                  onChange={(e) => handlePhoneToggle(e.target.checked)}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings Card - Only show when phone is enabled */}
        {isActive && (
          <Card
            style={{
              width: "100%",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              boxShadow: "none",
              marginTop: spacingToPx(2),
            }}
          >
            <CardContent>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography variant="body1" color="primary">
                    Notification
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginTop: spacingToPx(0.5),
                    }}
                  >
                    {"Show notifications with phone's system notification bar."}
                  </Typography>
                </div>
                {notificationLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <MSwitch
                    checked={notificationEnabled}
                    onChange={(e) => handleNotificationToggle(e.target.checked)}
                    disabled={!isActive}
                  />
                )}
              </div>
              {notificationEnabled && (
                <div style={{ marginTop: spacingToPx(2) }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: spacingToPx(1),
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notificationPriorities.includes("high")}
                          onChange={(e) =>
                            handleNotificationPriorityChange(
                              "high",
                              e.target.checked
                            )
                          }
                          size="small"
                        />
                      }
                      label={
                        <div>
                          <Typography variant="body2">High</Typography>
                          <Typography
                            variant="caption"
                            className="text-muted-foreground"
                          >
                            {
                              "Show High priority notifications. Each notification's priority is set in the automation configuration."
                            }
                          </Typography>
                        </div>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notificationPriorities.includes("low")}
                          onChange={(e) =>
                            handleNotificationPriorityChange(
                              "low",
                              e.target.checked
                            )
                          }
                          size="small"
                        />
                      }
                      label={
                        <div>
                          <Typography variant="body2">Low</Typography>
                          <Typography
                            variant="caption"
                            className="text-muted-foreground"
                          >
                            {
                              "Show Low priority notifications. Each notification's priority is set in the automation configuration."
                            }
                          </Typography>
                        </div>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notificationPriorities.includes("dialog")}
                          onChange={(e) =>
                            handleNotificationPriorityChange(
                              "dialog",
                              e.target.checked
                            )
                          }
                          size="small"
                        />
                      }
                      label={
                        <div>
                          <Typography variant="body2">Dialog</Typography>
                          <Typography
                            variant="caption"
                            className="text-muted-foreground"
                          >
                            {
                              "Show Dialog notifications. Dialog notifications require user response."
                            }
                          </Typography>
                        </div>
                      }
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Report Card - Only show when phone is enabled */}
        {isActive && (
          <Card
            style={{
              width: "100%",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              boxShadow: "none",
              marginTop: spacingToPx(2),
            }}
          >
            <CardContent>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography variant="body1" color="primary">
                    Mode
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginTop: spacingToPx(0.5),
                    }}
                  >
                    If to continuously run App for more phone data capabilities.
                  </Typography>
                </div>
                {dataReportLoading && <CircularProgress size={24} />}
              </div>
              <div style={{ marginTop: spacingToPx(2) }}>
                <RadioGroup
                  value={dataReportMode}
                  onChange={(e) => handleDataReportModeChange(e.target.value)}
                  disabled={!isActive || dataReportLoading}
                  style={{
                    gap: spacingToPx(1.5),
                  }}
                >
                  <FormControlLabel
                    value="default"
                    control={<Radio size="small" />}
                    label={
                      <div>
                        <Typography variant="body2">Default</Typography>
                        <Typography
                          variant="caption"
                          className="text-muted-foreground"
                        >
                          Provide limited phone capabilities.
                        </Typography>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="realtime"
                    control={<Radio size="small" />}
                    label={
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacingToPx(1),
                          }}
                        >
                          <Typography variant="body2">Advance</Typography>
                          <Chip
                            label="Not Available"
                            size="small"
                            style={{
                              height: "20px",
                              fontSize: "0.7rem",
                            }}
                          />
                        </div>
                        <Typography
                          variant="caption"
                          className="text-muted-foreground"
                        >
                          Keep the App alive in the background to support more
                          phone capabilities.
                        </Typography>
                      </div>
                    }
                    disabled
                  />
                </RadioGroup>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Categories Card */}
        {isActive && fullCategories.length > 0 && (
          <Card
            style={{
              width: "100%",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              boxShadow: "none",
              marginTop: spacingToPx(2),
            }}
          >
            <CardContent>
              <Typography variant="body1" color="primary">
                Data Categories
              </Typography>
              <Typography
                variant="caption"
                className="text-muted-foreground"
                style={{
                  display: "block",
                  marginBottom: spacingToPx(2),
                  marginTop: spacingToPx(0.5),
                }}
              >
                Control which types of phone data can be accessed by automations
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: spacingToPx(2),
                }}
              >
                {fullCategories.map((category, index) => {
                  const categoryEnabled = isCategoryEnabled(category.id);
                  const isExpanded = expandedCategories[category.id];
                  const hasCapabilities =
                    category.capabilities && category.capabilities.length > 0;

                  return (
                    <div key={category.id}>
                      {index > 0 && (
                        <div
                          style={{
                            height: "1px",
                            backgroundColor: "hsl(var(--border))",
                            marginBottom: spacingToPx(2),
                          }}
                        />
                      )}
                      <div
                        style={{
                          opacity: isActive ? 1 : 0.6,
                          transition: "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            minHeight: "48px",
                            cursor: hasCapabilities ? "pointer" : "default",
                            WebkitTapHighlightColor: "transparent",
                          }}
                          onClick={
                            hasCapabilities
                              ? () => toggleCategoryExpansion(category.id)
                              : undefined
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            {hasCapabilities && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCategoryExpansion(category.id);
                                }}
                                style={{
                                  marginRight: spacingToPx(1),
                                  width: "32px",
                                  height: "32px",
                                }}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            )}
                            {!hasCapabilities && (
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  marginRight: spacingToPx(1),
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                style={{
                                  fontWeight: 500,
                                }}
                              >
                                {category.name || category.displayName}
                              </Typography>
                              {category.description && (
                                <Typography
                                  variant="caption"
                                  style={{
                                    display: "block",
                                    marginTop: spacingToPx(0.25),
                                  }}
                                >
                                  {category.description}
                                </Typography>
                              )}
                            </div>
                          </div>
                          {categoryLoading[category.id] ? (
                            <CircularProgress size={24} />
                          ) : (
                            <FormControlLabel
                              control={
                                <MSwitch
                                  size="small"
                                  checked={categoryEnabled}
                                  onChange={(e) =>
                                    handleCategoryToggle(
                                      category.id,
                                      e.target.checked
                                    )
                                  }
                                  disabled={!isActive}
                                />
                              }
                              label=""
                              onClick={(event) => event.stopPropagation()}
                            />
                          )}
                        </div>

                        {/* Capabilities */}
                        {hasCapabilities && (
                          <Collapse in={isExpanded}>
                            <div
                              style={{
                                marginTop: spacingToPx(1),
                                paddingLeft: spacingToPx(5),
                                paddingTop: spacingToPx(1),
                                paddingBottom: spacingToPx(1),
                              }}
                            >
                              {category.capabilities.map((capabilityObj) => {
                                const capabilityEnabled = isCapabilityEnabled(
                                  capabilityObj.capability
                                );
                                const requiresAdvanceMode =
                                  capabilityObj.requireRealtime &&
                                  dataReportMode !== "realtime";

                                return (
                                  <div
                                    key={capabilityObj.capability}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      minHeight: "48px",
                                      paddingTop: spacingToPx(1),
                                      paddingBottom: spacingToPx(1),
                                      opacity: requiresAdvanceMode ? 0.5 : 1,
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: spacingToPx(1),
                                          marginBottom: spacingToPx(0.5),
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          style={{
                                            fontWeight: 500,
                                            color: "hsl(var(--foreground))",
                                          }}
                                        >
                                          {capabilityObj.name}
                                        </Typography>
                                        {requiresAdvanceMode && (
                                          <Chip
                                            label="Require advance mode"
                                            size="small"
                                            style={{
                                              height: "20px",
                                              fontSize: "0.7rem",
                                            }}
                                          />
                                        )}
                                      </div>
                                      {capabilityObj.important && (
                                        <Typography
                                          variant="caption"
                                          style={{
                                            color: "#FFD700",
                                            display: "block",
                                            fontWeight: 500,
                                            marginBottom: spacingToPx(0.5),
                                          }}
                                        >
                                          {capabilityObj.important}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        style={{
                                          color: "hsl(var(--muted-foreground))",
                                        }}
                                      >
                                        {capabilityObj.description}
                                      </Typography>
                                    </div>
                                    {!requiresAdvanceMode &&
                                      (capabilityLoading[
                                        capabilityObj.capability
                                      ] ? (
                                        <CircularProgress size={24} />
                                      ) : (
                                        <FormControlLabel
                                          control={
                                            <MSwitch
                                              size="small"
                                              checked={capabilityEnabled}
                                              onChange={(e) =>
                                                handleCapabilityToggle(
                                                  capabilityObj.capability,
                                                  e.target.checked
                                                )
                                              }
                                              disabled={
                                                !isActive || !categoryEnabled
                                              }
                                            />
                                          }
                                          label=""
                                        />
                                      ))}
                                  </div>
                                );
                              })}
                            </div>
                          </Collapse>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card
          style={{
            marginTop: spacingToPx(2),
            marginBottom: spacingToPx(8),
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
          }}
        >
          <CardContent>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <InfoOutlinedIcon
                style={{
                  color: "hsl(var(--primary))",
                  marginRight: spacingToPx(2),
                }}
              />
              <div>
                <Typography variant="body2" color="textSecondary">
                  Attention! By enabling the phone permissions, you are exposing
                  this phone to the current space.
                  <br />
                  <br />
                  For example, by just enabling the phone device, users in the
                  same space will know this device exists, but no real data is
                  shared.
                  <br />
                  But if you enabled a specific category/capability, automations
                  can access the data, which also means other users in the space
                  can access that data.
                  <br />
                  <br />
                  The permission setting is only effective within the current
                  space.
                  <br />
                  <br />
                  When signing out, the permissions are revoked automatically.
                  <br />
                  <br />
                  When switching to a different space, this space&apos;s
                  permissions are still effective. It means you still get the
                  space&apos;s notifications, and the automations still run with
                  the phone data, even if you are active in your another space.
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PhonePermission;
